import { useMutation } from "@apollo/client/react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";

import { constantsData, identifiersData, namesData, statusTypesData } from "@/data";
import { deleteUserMutation } from "@/graph";
import { openAlert } from "@/redux/actions";
import { displayErrorHandler } from "@/utils";

const useDeleteUser = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const [deleteUserAction, { loading }] = useMutation(deleteUserMutation);

    const { deleteUserPending: deleteUserPendingToastName } = namesData.toasts;

    const { toast: toastId } = identifiersData.ids;

    const deleteUser = async (id) => {
        try {
            await deleteUserAction({
                update(cache) {
                    cache.modify({
                        fields: {
                            listUsers(existingData, { readField }) {
                                if (!existingData?.posts) return existingData;
                                const filteredPosts = existingData.posts.filter(
                                    (ref) => readField("user_id", ref) !== id,
                                );
                                if (filteredPosts.length === existingData.posts.length) return existingData;

                                return {
                                    ...existingData,
                                    posts: filteredPosts,
                                    meta: {
                                        ...existingData.meta,
                                        resultCount: existingData.meta.resultCount - 1,
                                        nodeCount: existingData.meta.nodeCount - 1,
                                    },
                                };
                            },
                        },
                    });

                    cache.evict({ id: `User:${id}` });
                    cache.gc();
                },
                onCompleted: () => {
                    dispatch(
                        openAlert(
                            t("messages.deleteMsgs.success", { entity: t("users.entity") }),
                            statusTypesData.success,
                        ),
                    );
                },
                variables: {
                    where: { id },
                },
            });
        } catch (err) {
            displayErrorHandler(
                err.message === constantsData.failedToFetch ? t("messages.networkFailure") : err.message,
                dispatch,
                openAlert,
            );
        } finally {
            toast.dismiss(`${deleteUserPendingToastName}_${toastId}`);
        }
    };

    useEffect(
        () => {
            if (loading) {
                toast.info(() => t("messages.deleteMsgs.pending", { entity: t("users.entity") }), {
                    toastId: `${deleteUserPendingToastName}_${toastId}`,
                });
            }
        },
        [loading], //eslint-disable-line
    );

    return { deleteUser };
};

export default useDeleteUser;
